/**
 * @file Comments component
 * @author Heino Lenting
 */
import * as tslib_1 from "tslib";
import { OnInit, OnDestroy } from '@angular/core';
import { AlertsService } from '../../core/alerts.service';
import { UserService } from '../../user.service';
import { CommentService } from '../comment.service';
import { ActivatedRoute } from '@angular/router';
import { FormControl } from '@angular/forms';
import { InputTextComponent } from '../../base/inputs/input-text/input-text.component';
/**
 * Class for the comments component
 */
var CommentsComponent = /** @class */ (function () {
    /**
     * Constructs a CommentsComponent
     */
    function CommentsComponent(userService, alertsService, commentService, route) {
        this.userService = userService;
        this.alertsService = alertsService;
        this.commentService = commentService;
        this.route = route;
        this.formControl = new FormControl();
        /** Max characters of the input */
        this.maxCharacters = 1000;
        /** Total comment of the entity */
        this.totalItems = 0;
        /** The comments */
        this._items = [];
        /** Page size of the list */
        this.pageSize = 10;
        /** Number of items shown */
        this.nrItemsShown = 0;
        this.configuration = {
            instruction: 'instructions/standaard/core/social/comments/comments'
        };
        /** Refresh data after */
        this.timer = 10000;
    }
    Object.defineProperty(CommentsComponent.prototype, "entityKey", {
        get: function () {
            return this._entityKey;
        },
        set: function (value) {
            if (!value) {
                return;
            }
            var key = value.split('_')[0];
            this._entityKey = key;
            this.refresh();
            this.formControl.setValue('');
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Is called directly after the creation of this component
     */
    CommentsComponent.prototype.ngOnInit = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: 
                    // Get the comments from the db
                    return [4 /*yield*/, this.refresh()];
                    case 1:
                        // Get the comments from the db
                        _a.sent();
                        // Quick sort
                        this.items.sort(function (a, b) {
                            return +new Date(a.created) - +new Date(b.created);
                        });
                        this.nrItemsShown = this.pageSize;
                        this.refreshData();
                        return [2 /*return*/];
                }
            });
        });
    };
    Object.defineProperty(CommentsComponent.prototype, "user", {
        /** Gets the user info of the authenticated user. */
        get: function () {
            return this.userService.user;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CommentsComponent.prototype, "items", {
        /** Gets the items w.r.t. currentPage and pageSize */
        get: function () {
            return this._items.slice(-this.nrItemsShown);
        },
        /** Set the items from the database */
        set: function (value) {
            if (!value) {
                return;
            }
            this._items = value;
        },
        enumerable: true,
        configurable: true
    });
    /** Checks if the comment text input is not empty */
    CommentsComponent.prototype.hasValue = function (comment) {
        return comment !== '';
    };
    /** Checks if there are more items available */
    CommentsComponent.prototype.hasMore = function () {
        return this.nrItemsShown < this._items.length;
    };
    /** Show also the next page of comment */
    CommentsComponent.prototype.showMore = function () {
        if (this.hasMore()) {
            var nrToShow = this.nrItemsShown + this.pageSize;
            if (nrToShow > this._items.length) {
                nrToShow = this._items.length;
            }
            this.nrItemsShown = nrToShow;
        }
    };
    /**
     * Private method to delete a comment
     * @param id The ID of the comment
     */
    CommentsComponent.prototype.delete = function (id) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.commentService.removeComment(id)];
                    case 1: return [2 /*return*/, _a.sent()];
                }
            });
        });
    };
    /**
     * Method to validate a comment
     * @param comment The comment text
     */
    CommentsComponent.prototype.validate = function (comment) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            if (comment === '' || comment.length > _this.maxCharacters) {
                return reject(new Error("Validation failed: The lengh of the comment must be between 1 and " + _this.maxCharacters + " signs"));
            }
            resolve();
        });
    };
    /**
     * Method to save a comment
     * @param comment The comment text
     */
    CommentsComponent.prototype.save = function (comment) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.commentService.createComment(this.entityKey, comment, this.userService)];
                    case 1: return [2 /*return*/, _a.sent()];
                }
            });
        });
    };
    /**
     * Method to remove a comment, called from template
     * @param id The ID of the comment
     */
    CommentsComponent.prototype.remove = function (id) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var message, error_1;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 3, , 4]);
                        return [4 /*yield*/, this.delete(id)];
                    case 1:
                        message = _a.sent();
                        return [4 /*yield*/, this.refresh()];
                    case 2:
                        _a.sent();
                        this.alertsService.createAlert('success', message);
                        return [3 /*break*/, 4];
                    case 3:
                        error_1 = _a.sent();
                        this.alertsService.createAlert('warning', 'APP.SOCIAL.COMMENT_UNSUCCESSFULLY_DELETED', error_1.message);
                        return [3 /*break*/, 4];
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    /**
    * Is there a comment in the markdown editor
    */
    CommentsComponent.prototype.hasComment = function () {
        if (!this.formControl.value) {
            return false;
        }
        return (this.formControl.value !== '');
    };
    /**
    * Cancel (=reset) the comment
    */
    CommentsComponent.prototype.cancel = function () {
        this.formControl.setValue('');
    };
    /**
     * Method to add a comment
     */
    CommentsComponent.prototype.add = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var comment, message, error_2;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        comment = this.formControl.value;
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 5, , 6]);
                        return [4 /*yield*/, this.validate(comment)];
                    case 2:
                        _a.sent();
                        return [4 /*yield*/, this.save(comment)];
                    case 3:
                        message = _a.sent();
                        return [4 /*yield*/, this.refresh()];
                    case 4:
                        _a.sent();
                        this.nrItemsShown++;
                        this.alertsService.createAlert('success', message);
                        this.formControl.setValue('');
                        this.inputText.mode = 'edit'; // Set input text in correct mode (if it was in preview mode during save)
                        return [3 /*break*/, 6];
                    case 5:
                        error_2 = _a.sent();
                        this.alertsService.createAlert('warning', 'APP.SOCIAL.COMMENT_UNSUCCESSFULLY_ADDED', error_2.message);
                        return [3 /*break*/, 6];
                    case 6: return [2 /*return*/];
                }
            });
        });
    };
    /** Method that returns the fullname of this user */
    CommentsComponent.prototype.getThisUser = function () {
        return this.userService.user.fullName;
    };
    /** Method that returns the fullname of this user */
    CommentsComponent.prototype.canRemove = function (item) {
        var userRole = this.userService.html5role;
        if (userRole !== 'employee' &&
            userRole !== 'planner') {
            return true;
        }
        var userOid = this.userService.user['Oid'];
        if (userOid === item.userOid) {
            return true;
        }
        return false;
    };
    /** Method that returns the fullname of the user */
    CommentsComponent.prototype.getFullName = function (item) {
        var fullName = item.user.fullName;
        var fallBack = item.user.Name;
        return fullName !== ' ' ? fullName : fallBack;
    };
    /** Get the comments from the database */
    CommentsComponent.prototype.refresh = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var comments, error_3;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, this.commentService.getComments(this.entityKey)];
                    case 1:
                        comments = _a.sent();
                        if (comments) {
                            this.items = comments;
                            this.totalItems = comments.length;
                        }
                        return [3 /*break*/, 3];
                    case 2:
                        error_3 = _a.sent();
                        this.alertsService.createAlert('warning', 'APP.SOCIAL.COMMENT_UNSUCCESSFULLY_LOADED', error_3.message);
                        return [3 /*break*/, 3];
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    /** Set Interval */
    CommentsComponent.prototype.refreshData = function () {
        var _this = this;
        this.refresher =
            setInterval(function () {
                _this.refresh();
            }, this.timer);
    };
    /** Clear Interval */
    CommentsComponent.prototype.cancelPageRefresh = function () {
        if (this.refresher) {
            clearInterval(this.refresher);
        }
    };
    /** Cleanup just before Angular destroys the directive/component.
     * Unsubscribe Observables and detach event handlers to avoid memory leaks.
     * */
    CommentsComponent.prototype.ngOnDestroy = function () {
        this.cancelPageRefresh();
    };
    return CommentsComponent;
}());
export { CommentsComponent };
